import { createTheme } from '@mui/material/styles';

// El panel antiguo (admin.easydebt.es) también en modo oscuro (André, 28/08).
//
// La variante clara es EXACTAMENTE la de siempre: el portal de acreedores
// comparte este archivo y no debe cambiarle nada. Lo oscuro se aplica solo
// donde hace falta, con los colores fijos del tema claro sustituidos por los
// de la paleta cuando el modo es 'dark'.
// Paleta de la guía de identidad de EasyDebt (André, 28/08): el acento del
// panel interno pasa del turquesa al azul de la marca. El portal de acreedores
// NO cambia: es la pantalla que ven desde fuera y nadie lo ha pedido.
const MARCA = {
  azul: '#0E5AE9', // color principal
  azulClaro: '#518BFE', // secundario: el que se lee sobre fondo oscuro
  azulSuave: '#9BBBFE',
  azulOscuro: '#022369', // color de contraste
};
const TURQUESA = { main: '#00B5AD', light: '#33C3BC', dark: '#007E79', contrastText: '#ffffff' };

const crearTema = (mode = 'light', vista = 'admin') => {
  const oscuro = mode === 'dark';
  const acreedores = vista === 'creditor';

  // Sobre fondo oscuro el azul de marca se queda corto de contraste, así que
  // se usa el azul secundario con texto oscuro encima (como hace MUI).
  const acento = acreedores
    ? TURQUESA
    : oscuro
      ? { main: MARCA.azulClaro, light: MARCA.azulSuave, dark: MARCA.azul, contrastText: '#08142c' }
      : { main: MARCA.azul, light: MARCA.azulClaro, dark: MARCA.azulOscuro, contrastText: '#ffffff' };

  // Tintes del acento (fondos suaves, sombras): se derivan del color elegido
  // para que no quede turquesa suelto cuando el panel va en azul.
  const tinte = (op) => {
    const h = acento.main.replace('#', '');
    const n = parseInt(h, 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${op})`;
  };

  // "Correcto/subido/verificado" se pinta en verde de verdad: si se quedara en
  // turquesa parecería un resto del color antiguo al lado del azul.
  const correcto = acreedores
    ? TURQUESA
    : oscuro
      ? { main: '#4CAF50', light: '#81C784', dark: '#2E7D32', contrastText: '#08142c' }
      : { main: '#2E7D32', light: '#4CAF50', dark: '#1B5E20', contrastText: '#ffffff' };

  // Gris azulado, ni negro puro ni gris plano: es el mismo tono que ya usan
  // ANDI y el panel de WhatsApp, para que las tres herramientas se parezcan.
  const fondo = oscuro ? '#161c27' : '#F8FAFB';
  const tarjeta = oscuro ? '#1d2431' : '#ffffff';
  const textoPrincipal = oscuro ? '#e6e9f0' : '#2C3E50';
  const textoSecundario = oscuro ? 'rgba(230, 233, 240, 0.68)' : 'rgba(44, 62, 80, 0.7)';
  const textoCuerpo = oscuro ? 'rgba(230, 233, 240, 0.87)' : 'rgba(44, 62, 80, 0.87)';
  const borde = oscuro ? 'rgba(230, 233, 240, 0.14)' : 'rgba(44, 62, 80, 0.1)';
  const sombra = oscuro ? '0 4px 20px rgba(0, 0, 0, 0.35)' : '0 4px 20px rgba(44, 62, 80, 0.05)';

  return createTheme({
    palette: {
      mode,
      primary: acento,
      secondary: {
        main: oscuro ? '#7f8fa6' : '#2C3E50',
        light: oscuro ? '#9aa8bb' : '#34495E',
        dark: oscuro ? '#5d6b80' : '#1A252F',
        contrastText: '#ffffff',
      },
      success: correcto,
      error: {
        main: '#E74C3C',
        light: '#FF6B6B',
        dark: '#C0392B',
        contrastText: '#ffffff',
      },
      warning: {
        main: '#F39C12',
        light: '#F1C40F',
        dark: '#D35400',
        contrastText: '#ffffff',
      },
      info: {
        main: '#3498DB',
        light: '#5DADE2',
        dark: '#2980B9',
        contrastText: '#ffffff',
      },
      background: {
        default: fondo,
        paper: tarjeta,
      },
      text: {
        primary: textoPrincipal,
        secondary: textoSecundario,
      },
      divider: borde,
    },
    typography: {
      fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
      // En oscuro no se fija color en los títulos: manda la paleta, si no
      // saldrían en azul marino sobre fondo oscuro.
      h1: { fontSize: '2.5rem', fontWeight: 600, lineHeight: 1.2, ...(oscuro ? {} : { color: '#2C3E50' }) },
      h2: { fontSize: '2rem', fontWeight: 600, lineHeight: 1.2, ...(oscuro ? {} : { color: '#2C3E50' }) },
      h3: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.2, ...(oscuro ? {} : { color: '#2C3E50' }) },
      h4: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.2, ...(oscuro ? {} : { color: '#2C3E50' }) },
      h5: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.2, ...(oscuro ? {} : { color: '#2C3E50' }) },
      h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.2, ...(oscuro ? {} : { color: '#2C3E50' }) },
      subtitle1: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.5, color: textoCuerpo },
      subtitle2: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.5, color: textoCuerpo },
      body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.5, color: textoCuerpo },
      body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5, color: textoCuerpo },
      button: { fontSize: '0.875rem', fontWeight: 500, textTransform: 'none' },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            padding: '10px 24px',
            textTransform: 'none',
            fontWeight: 600,
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: `0 4px 12px ${tinte(0.2)}`,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: sombra,
            ...(oscuro ? { backgroundImage: 'none' } : {}),
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: sombra,
            ...(oscuro ? { backgroundImage: 'none' } : {}),
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 12,
              '&:hover fieldset': {
                borderColor: acento.main,
              },
              '&.Mui-focused fieldset': {
                borderColor: acento.main,
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
          },
          filled: {
            backgroundColor: tinte(0.1),
            color: oscuro ? acento.light : acento.main,
            '&:hover': {
              backgroundColor: tinte(0.2),
            },
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            '&:hover': {
              backgroundColor: tinte(0.04),
            },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              fontWeight: 600,
              backgroundColor: tinte(oscuro ? 0.12 : 0.04),
              color: textoPrincipal,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '16px 24px',
            borderBottom: `1px solid ${borde}`,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: tarjeta,
            color: textoPrincipal,
            boxShadow: oscuro ? '0 2px 12px rgba(0, 0, 0, 0.4)' : '0 2px 12px rgba(44, 62, 80, 0.05)',
            ...(oscuro ? { backgroundImage: 'none' } : {}),
          },
        },
      },
    },
  });
};

export { crearTema };

const theme = crearTema('light');

export default theme;
